import { Analytics } from '@cobliteam/analytics';
import { Location } from '@cobliteam/common-types';
import type { AiRecommendationJourney } from '@cobliteam/hooks/src/useAiRecommendationsStore/useAiRecommendationsStore';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags/useFeatureFlags';
import Button from '@cobliteam/ui/components/Button';
import Icon from '@cobliteam/ui/icons';
import { useHistory } from 'react-router-dom';
import AIIcon from '../components/AIIcon';
import { useSchedulingAgent } from '../store';

const CTA_COLOR = '#0234E1';

const JOURNEY_LOCATIONS: Record<AiRecommendationJourney, string> = {
  'driver-registration': Location.DRIVER_MANAGEMENT,
  'setup-driver-association': Location.DRIVER_MANAGEMENT,
  'vehicle-groups': Location.VEHICLES_GROUPS,
  'setup-groups': Location.VEHICLES_GROUPS,
  installation: Location.ORDERS,
  checklists: Location.CHECKLIST_ALL,
  'fleet-policies': Location.ALERTS_TRIGGERS_CREATE,
  geofences: Location.GEOFENCES_CREATE,
  'wifi-configuration': Location.CONFIG_CAMERA_WIFI_CONFIGURATION,
  'cam-audio': Location.CONFIG_ALERTS,
  'sensibility-configuration': Location.CONFIG_EVENTS_CONFIGURATION,
  'user-profiles': Location.CONFIG_USERS,
};

interface UseAiRecommendationCtaOptions {
  component: string;
  journeyId: AiRecommendationJourney;
  aiPrompt?: string;
  /** Label shown on the AI CTA button. Defaults to 'Ajuda com a Cobli AI'. */
  label?: string;
  /** Custom action when AI CTA is clicked. Overrides default navigation to /ai. */
  action?: () => void;
  /** Label shown for the CTA. Defaults to 'Ir para a página'. */
  fallbackLabel?: string;
  /** Action called when the CTA is clicked. Defaults to navigating to the journey page. */
  fallbackAction?: () => void;
  /** When true, ignores the `aiChat` flag so non-installation journeys always use the page-navigation fallback. */
  disableAiChat?: boolean;
}

const useAiRecommendationCta = ({ component, journeyId, aiPrompt, label, action, fallbackLabel, fallbackAction, disableAiChat = false }: UseAiRecommendationCtaOptions) => {
  const history = useHistory();
  const { aiChat } = useFeatureFlags();
  const openSchedulingAgent = useSchedulingAgent((s) => s.open);

  const isInstallation = journeyId === 'installation';
  const aiChatEnabled = aiChat && !disableAiChat;
  const showAiCta = aiChatEnabled || isInstallation;

  const effectiveLabel = label ?? (isInstallation ? 'Agendar com IA' : 'Ajuda com a Cobli AI');

  const onCta = () => {
    Analytics.track('Ai Recommendation call to action clicked', {
      component: component,
      journeyId: journeyId,
    });

    if (isInstallation) {
      Analytics.track('Scheduling Agent opened', { component: 'ai-recommendation-cta', journeyId });
      openSchedulingAgent('Quero agendar minhas instalações pendentes');
      return;
    }

    if (action && aiChatEnabled) {
      action();
    } else if (aiChatEnabled) {
      history.push(`${Location.AI}?prompt=${encodeURIComponent(aiPrompt ?? 'Como posso maximizar o valor da minha frota com a plataforma Cobli?')}`);
    } else if (fallbackAction) {
      fallbackAction();
    } else {
      const location = JOURNEY_LOCATIONS[journeyId];
      if (location) history.push(location);
    }
  };

  const ctaButton = showAiCta ? (
    <button
      type='button'
      onClick={onCta}
      className='flex items-center gap-2 rounded-xl px-4 py-2 font-semibold text-sm text-white transition-opacity hover:opacity-90'
      style={{ background: CTA_COLOR }}
    >
      {!isInstallation && <AIIcon size={22} sparklesPaint='white' />}
      {effectiveLabel}
      {isInstallation && <Icon name='sparkles' className='text-white' />}
    </button>
  ) : (
    <Button variant='contained' color='primary' onClick={onCta}>
      {fallbackLabel ?? 'Ir para a página'}
    </Button>
  );

  return { ctaButton };
};

export default useAiRecommendationCta;
